import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '@/api/client'
import { Banner, ErrorNote, Spinner } from '@/components/ui'
// One definition, shared with `useChannel()` — the People forms branch on the same
// shape this page renders.
import type { ChannelOption as Option, ChannelStatus as Status } from '@/lib/channel'

// What each channel costs and what it asks of people. Stated on the screen because
// this is the decision the page exists for, and the trade is not obvious: one costs
// money, the other costs everybody installing an app.
const NOTES: Record<string, { good: string[]; cost: string[] }> = {
  whatsapp: {
    good: ['Everybody already has WhatsApp',
           'Delivery and read receipts',
           'Nothing for staff to install or set up'],
    cost: ['Every message the app starts is billed',
           'Six message templates need Meta’s approval',
           'Business verification to go past five people'],
  },
  telegram: {
    good: ['Free — no per-message charge at all',
           'No templates and no approval to wait for',
           'No limit on how many people'],
    cost: ['Everybody must install Telegram and tap an invite link',
           'No delivery or read receipts — messages stop at “sent”',
           'Less familiar than WhatsApp for business in India'],
  },
}

export function Channel() {
  const qc = useQueryClient()
  const status = useQuery({ queryKey: ['channel'], queryFn: () => api.get<Status>('/channel') })
  const [err, setErr] = useState<unknown>(null)
  const [pending, setPending] = useState<Option | null>(null)

  const save = useMutation({
    mutationFn: (v: { channel: string; force?: boolean }) => api.put('/channel', v),
    onSuccess: () => {
      setErr(null); setPending(null)
      qc.invalidateQueries({ queryKey: ['channel'] })
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: setErr,
  })

  if (status.isLoading) return <Spinner />
  if (status.error) return <ErrorNote error={status.error} />

  const opts = status.data?.options ?? []
  const active = status.data?.active
  const conflict = err instanceof ApiError && err.code === 'WOULD_STRAND_USERS'
    ? err : null
  const stranded = (conflict?.details?.stranded ?? []) as
    { user_id: string; name: string; why: string }[]

  return (
    <>
      <div className="card mb-4">
        <div className="card-head">
          <b className="text-[13px]">Message channel</b>
          <span className="flex-1" />
          <span className="text-[11.8px] text-ink-3">
            One channel for the whole firm · changing it needs a restart
          </span>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {opts.map((o) => {
            const live = o.value === active
            const notes = NOTES[o.value]
            return (
              <div key={o.value}
                   className={`rounded-lg border p-4 ${live
                     ? 'border-primary bg-primary/[0.03]' : 'border-rule'}`}>
                <div className="flex items-baseline gap-2">
                  <b className="text-[14px]">{o.label}</b>
                  {live ? (
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider
                                     text-primary">in use</span>
                  ) : null}
                  <span className="flex-1" />
                  {!o.configured ? (
                    <span className="text-[11.5px] text-pend-ink">no credentials</span>
                  ) : null}
                </div>

                <div className="mt-2.5 text-[12.5px]">
                  <b className="font-semibold">{o.ready}</b> of {o.of} active{' '}
                  {o.of === 1 ? 'person' : 'people'} set up
                  {o.ready < o.of ? (
                    <ul className="mt-1.5 space-y-0.5">
                      {o.not_ready.slice(0, 5).map((u) => (
                        <li key={u.user_id} className="text-[11.5px] text-ink-3">
                          {u.name} — {u.why.replace(/^Will not receive [^:]*: /, '')}
                        </li>
                      ))}
                      {o.not_ready.length > 5 ? (
                        <li className="text-[11.5px] text-ink-3">
                          and {o.not_ready.length - 5} more
                        </li>
                      ) : null}
                    </ul>
                  ) : null}
                </div>

                {notes ? (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11.5px]">
                    <ul className="space-y-0.5">
                      {notes.good.map((t) => (
                        <li key={t} className="text-done-ink">+ {t}</li>
                      ))}
                    </ul>
                    <ul className="space-y-0.5">
                      {notes.cost.map((t) => (
                        <li key={t} className="text-ink-3">− {t}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {!live ? (
                  <button className="btn btn-sm mt-3.5" disabled={save.isPending}
                          onClick={() => { setErr(null); setPending(o)
                                           save.mutate({ channel: o.value }) }}>
                    Switch to {o.label}
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      {/* The guard. Refusing by default and naming who would go silent is the whole
          value here — a switch that quietly stops somebody's messages is the failure
          this page exists to prevent. */}
      {conflict ? (
        <div className="card">
          <div className="card-head"><b className="text-[13px]">Not switched</b></div>
          <div className="p-4 space-y-3">
            <Banner tone="bad">{conflict.message}</Banner>
            <div className="table-wrap">
              <table className="w-full border-collapse">
                <thead><tr>
                  <th className="th">Person</th><th className="th">Why</th>
                </tr></thead>
                <tbody>
                  {stranded.map((u) => (
                    <tr key={u.user_id}>
                      <td className="td">{u.name}</td>
                      <td className="td text-[12.5px] text-ink-3">
                        {u.why.replace(/^Will not receive [^:]*: /, '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[12.5px]">
              {pending?.value === 'telegram'
                ? 'Invite them on the People page first — each person has to tap the link themselves.'
                : 'Give them a phone number and opt-in on the People page first.'}
            </p>
            <div className="flex gap-2">
              <button className="btn btn-bad" disabled={save.isPending}
                      onClick={() => pending && save.mutate({
                        channel: pending.value, force: true })}>
                Switch anyway — they get nothing until fixed
              </button>
              <button className="btn" onClick={() => { setErr(null); setPending(null) }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : err ? <ErrorNote error={err} /> : null}

      {save.isSuccess && !conflict ? (
        <Banner tone="info">
          Saved. Restart the app to apply it:{' '}
          <code>docker compose up -d --force-recreate api</code>
        </Banner>
      ) : null}
    </>
  )
}
